import { useCallback, useState } from 'react';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';
import { fymAPI, useCreateWizardPresaveLinkMutation, usePublishSmartLinkMutation } from '../../../redux/api/fymAPI';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { SMLAnalytics } from '../../analytics/sml/analytics';
import { parseM24LimitError } from '../../music24/utils/parseM24LimitError';
import { presaveCreateInput } from './presaveCreateFacts';
import { smartLinkThemeOfRelease } from '../releaseTheme/smartLinkThemeOfRelease';

/** The made page (design presave-create b1): published, so the link works at once. */
export interface CreatedPresave {
  smartLinkId: number;
  /** The public address with its scheme. */
  shareUrl: string;
}

export type PresaveCreateOutcome =
  | { kind: 'created'; page: CreatedPresave }
  /** The page exists as a draft: publishing failed (a page limit, moderation). The Creator publishes it. */
  | { kind: 'draft'; smartLinkId: number }
  /** A plan limit: the global plan prompt opens (`customBaseQuery`), the screen stays as it is. */
  | { kind: 'limit' }
  | { kind: 'failed' };

/**
 * Makes the pre-save page of a song and publishes it (design presave-create a4 → b1). Two calls:
 * `POST .../wizards/presaveLink` with the facts of the row, then `POST .../smartLinks/:id/publish`.
 * The wizard endpoint makes a draft, and an artist who comes from a song wants a link to share.
 * The page takes the SmartLink theme of the song's release theme (`smartLinkThemeOfRelease`); a song
 * with no kit gets the default of the server.
 */
export function useCreatePresaveFromSong(accountId: number) {
  const [createWizardPresaveLink] = useCreateWizardPresaveLinkMutation();
  const [publishSmartLink] = usePublishSmartLinkMutation();
  const [loadReleaseKit] = fymAPI.endpoints.getReleaseKit.useLazyQuery();
  const [isCreating, setIsCreating] = useState(false);

  // Read at the click, not at the render: the onboarding makes the kit a moment before.
  const releaseThemeOf = useCallback(
    async (catalogItemUuid: string): Promise<SerializedSmartLinkVersionTheme | undefined> => {
      try {
        const answer = await loadReleaseKit({ accountId, catalogItemUuid }).unwrap();
        return smartLinkThemeOfRelease(answer.kit);
      } catch {
        // No kit answer: the page keeps the default theme.
        return undefined;
      }
    },
    [accountId, loadReleaseKit]
  );

  const create = useCallback(
    async (item: CatalogItem, services: string[]): Promise<PresaveCreateOutcome> => {
      setIsCreating(true);
      try {
        const theme = await releaseThemeOf(item.uuid);
        const created = await createWizardPresaveLink({
          accountId,
          ...presaveCreateInput(item, services),
          ...(theme === undefined ? {} : { theme }),
        });
        if (!isSuccessfulResult(created)) {
          return parseM24LimitError(created.error) === null ? { kind: 'failed' } : { kind: 'limit' };
        }
        const smartLinkId = created.data.smartLink.id;
        SMLAnalytics.trackSmartLinkCreated('presave').catch(console.error);
        const published = await publishSmartLink({ accountId, smartLinkId });
        const shareUrl = isSuccessfulResult(published) ? published.data.smartLink.shareUrl : null;
        if (shareUrl === null || shareUrl === '') {
          return { kind: 'draft', smartLinkId };
        }
        return { kind: 'created', page: { smartLinkId, shareUrl } };
      } catch (error) {
        console.error(error);
        return { kind: 'failed' };
      } finally {
        setIsCreating(false);
      }
    },
    [accountId, createWizardPresaveLink, publishSmartLink, releaseThemeOf]
  );

  return { create, isCreating };
}
